/**
 * 文件传输状态仓库。
 *
 * 原先传输列表和 file.* 事件监听都长在 FileTransfer 组件里，而侧栏默认停在「交流」页、
 * 文件面板根本没挂载 —— 于是收方收不到 file.offer，对方等到超时也没人接；
 * 切一下标签页，正在传的列表和进度也随组件卸载一起丢了。
 * 状态与监听跟着连接走（进房时 bind、离开时解绑），面板只负责显示。
 */

import { create } from 'zustand'

import type { Client } from '../lib/client'
import { MsgType, type FileDone, type FileOffer } from '../lib/messages'
import { USER_ALL } from '../lib/protocol'
import { newFileID, uploadFile } from '../lib/upload'

/** 一条传输记录。 */
export interface Transfer {
  fileId: string
  name: string
  size: number
  fromId: number
  destId: number
  /** 我是发送方还是接收方 */
  outgoing: boolean
  status: 'offering' | 'sending' | 'waiting' | 'done' | 'canceled' | 'failed'
  progress: number
  url?: string
  /** 失败原因，status 为 failed 时有值 */
  error?: string
}

/** bind 需要从房间那边拿的东西。用参数注入而不是直接 import roomStore，免得两个仓库互相引用。 */
export interface FileBindDeps {
  selfId: () => number
  /** 给用户一句提示（文件面板没打开时，收到文件要让人知道） */
  notify: (text: string) => void
  /** 按席位取名字，用于提示文案 */
  nameOf: (id: number) => string
}

interface FileState {
  items: Transfer[]
  /** 绑定到一条连接，返回解绑函数。换连接前必须解绑。 */
  bind: (client: Client, deps: FileBindDeps) => () => void
  send: (file: File, destId: number) => void
  accept: (fileId: string, ok: boolean) => void
  cancel: (fileId: string) => void
}

/** 当前绑定的连接。发送、应答都走它。 */
let bound: { client: Client; deps: FileBindDeps } | null = null

/** 等对方点「接收」的待发文件：fileId → 文件本体。 */
const pending = new Map<string, File>()

export const useFileStore = create<FileState>((set, get) => {
  const patch = (fileId: string, p: Partial<Transfer>) =>
    set((s) => ({ items: s.items.map((t) => (t.fileId === fileId ? { ...t, ...p } : t)) }))

  const statusOf = (fileId: string) => get().items.find((t) => t.fileId === fileId)?.status

  /** 真正开传。任何失败都落到列表里，不能让状态卡在「传输中」。 */
  const upload = async (fileId: string, file: File) => {
    const b = bound
    if (!b) return
    patch(fileId, { status: 'sending' })
    try {
      await uploadFile(b.client, fileId, file, (p) => {
        // 用户中途取消了就别再把进度刷回去
        if (statusOf(fileId) === 'sending') patch(fileId, { progress: p.ratio })
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : '传输失败'
      patch(fileId, { status: 'failed', error: msg })
      // 告诉对方别等了（连接还在的话）。
      b.client.send(MsgType.FileCancel, { fileId, byReceiver: false })
      b.deps.notify(`「${file.name}」发送失败：${msg}`)
    } finally {
      pending.delete(fileId)
    }
  }

  return {
    items: [],

    bind(client, deps) {
      bound = { client, deps }
      pending.clear()
      set({ items: [] })
      const offs = [
        client.on<FileOffer>(MsgType.FileOffer, (d) => {
          // 自己发的邀约会被服务端广播回来，跳过。
          if (d.fromId === deps.selfId()) return
          if (get().items.some((t) => t.fileId === d.fileId)) return
          set((s) => ({
            items: [
              ...s.items,
              {
                fileId: d.fileId,
                name: d.name,
                size: d.size,
                fromId: d.fromId,
                destId: d.destId,
                outgoing: false,
                status: 'offering',
                progress: 0,
              },
            ],
          }))
          // 面板多半没开着：说一声，否则文件就这么静静躺着，发的人一直等。
          deps.notify(`${deps.nameOf(d.fromId)} 发来文件「${d.name}」，请到「文件」页接收`)
        }),

        client.on<{ fileId: string; accept: boolean }>(MsgType.FileAccept, (d) => {
          const t = get().items.find((x) => x.fileId === d.fileId)
          if (!t) return
          if (!d.accept) {
            pending.delete(d.fileId)
            patch(d.fileId, { status: 'canceled' })
            return
          }
          // 我是发送方、正等着对方点接收：这时才开传。
          const file = pending.get(d.fileId)
          if (t.outgoing && t.status === 'waiting' && file) {
            void upload(d.fileId, file)
            return
          }
          if (t.status === 'offering' || t.status === 'waiting') patch(d.fileId, { status: 'sending' })
        }),

        client.on<FileDone>(MsgType.FileDone, (d) => {
          patch(d.fileId, { status: 'done', progress: 1, url: d.url })
        }),

        client.on<{ fileId: string }>(MsgType.FileCancel, (d) => {
          pending.delete(d.fileId)
          // 已完成的不改：对方事后点取消不该把能下载的文件变没。
          if (statusOf(d.fileId) !== 'done') patch(d.fileId, { status: 'canceled' })
        }),
      ]
      return () => {
        offs.forEach((f) => f())
        if (bound?.client === client) bound = null
        pending.clear()
      }
    },

    send(file, destId) {
      const b = bound
      if (!b) return
      const fileId = newFileID()
      const fromId = b.deps.selfId()
      set((s) => ({
        items: [
          ...s.items,
          { fileId, name: file.name, size: file.size, fromId, destId, outgoing: true, status: 'waiting', progress: 0 },
        ],
      }))
      b.client.send(MsgType.FileOffer, { fileId, fromId, destId, name: file.name, size: file.size })
      // 群发不必等谁点同意，直接开传；点对点等对方接受（见 FileAccept 监听）。
      if (destId === USER_ALL) {
        void upload(fileId, file)
        return
      }
      pending.set(fileId, file)
    },

    accept(fileId, ok) {
      bound?.client.send(MsgType.FileAccept, { fileId, accept: ok, startPos: 0 })
      patch(fileId, { status: ok ? 'sending' : 'canceled' })
    },

    cancel(fileId) {
      const t = get().items.find((x) => x.fileId === fileId)
      if (!t) return
      bound?.client.send(MsgType.FileCancel, { fileId, byReceiver: !t.outgoing })
      pending.delete(fileId)
      patch(fileId, { status: 'canceled' })
    },
  }
})
